import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  USER_IMPORT_TEMPLATE_OPTIONS,
  USER_IMPORT_NOTIFICATION_OPTIONS,
  type UserImportTemplate,
  type UserImportSflag,
} from '../constants/userImport';
import { apiClient } from '../lib/apiClient';
import { formatBackendDateTimeToLocal } from '../lib/dateTime';
import Modal from './Modal';
import Spinner from './Spinner';

type UserImportButtonProps = {
  onImportConfirm: (file: File, mode: UserImportSflag, template: UserImportTemplate | null) => void;
  isLoading: boolean;
};

function UserImportButton({ onImportConfirm, isLoading }: UserImportButtonProps) {
  const modalFileInputRef = useRef<HTMLInputElement>(null);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importSflag, setImportSflag] = useState<UserImportSflag>('email_only');
  const [importPriority, setImportPriority] = useState<UserImportTemplate | null>('emr');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isImportConfirmOpen, setIsImportConfirmOpen] = useState(false);
  const [isSyncDetailsOpen, setIsSyncDetailsOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const syncLogsQuery = useQuery({
    queryKey: ['users-sync-logs'],
    queryFn: () =>
      apiClient.getJson<
        Array<{
          id: number;
          fileName: string;
          rowsProcessed: number;
          failedSyncCount: number;
          passedCount: number;
          createdAt: string;
        }>
      >('/users/sync-logs'),
    enabled: isSyncDetailsOpen,
  });

  const resetImportModalState = useCallback(() => {
    setImportSflag('email_only');
    setImportPriority('emr');
    setPendingFile(null);
    const input = modalFileInputRef.current;
    if (input) {
      input.value = '';
    }
  }, []);

  const closeImportModal = useCallback(() => {
    setIsImportModalOpen(false);
    setIsImportConfirmOpen(false);
    resetImportModalState();
  }, [resetImportModalState]);

  const selectedNotificationLabel =
    USER_IMPORT_NOTIFICATION_OPTIONS.find((option) => option.value === importSflag)?.label ?? importSflag;
  const selectedTemplateLabel = importPriority
    ? USER_IMPORT_TEMPLATE_OPTIONS.find((option) => option.value === importPriority)?.label ?? importPriority
    : 'None';

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const handleModalFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) {
      setPendingFile(null);
      return;
    }
    setPendingFile(selectedFile);
  };

  const handleOpenImportConfirm = () => {
    if (!pendingFile) {
      return;
    }
    setIsImportConfirmOpen(true);
  };

  const handleFinalImportConfirm = () => {
    if (!pendingFile) {
      return;
    }
    onImportConfirm(pendingFile, importSflag, importPriority);
    closeImportModal();
  };

  const updateTooltipPosition = () => {
    const infoButton = infoButtonRef.current;
    if (!infoButton) {
      return;
    }

    const rect = infoButton.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  const showTooltip = () => {
    updateTooltipPosition();
    setIsTooltipOpen(true);
  };

  const hideTooltip = () => {
    setIsTooltipOpen(false);
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleOpenImportModal}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 disabled:cursor-not-allowed disabled:bg-white/70 disabled:text-indigo-300"
          >
            {isLoading && <Spinner size="sm" />}
            <span>{isLoading ? 'Importing Users...' : 'Import Users'}</span>
          </button>
          <div>
            <button
              ref={infoButtonRef}
              type="button"
              aria-label="Required user import columns"
              onMouseEnter={showTooltip}
              onMouseLeave={hideTooltip}
              onFocus={showTooltip}
              onBlur={hideTooltip}
              className="flex h-7 w-7 items-center justify-center rounded-full border border-white/50 bg-white/20 text-sm font-bold text-white transition hover:bg-white/30"
            >
              i
            </button>
          </div>
        </div>
        {isTooltipOpen &&
          tooltipPosition &&
          createPortal(
            <div
              className="pointer-events-none fixed z-[1000] w-42 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-lg"
              style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
            >
              Only .xlsx file is supported.
            </div>,
            document.body
          )}
        <button
          type="button"
          onClick={() => setIsSyncDetailsOpen(true)}
          className="rounded-md border border-white/50 bg-white/20 px-3 py-1 text-xs font-semibold text-white transition hover:bg-white/30"
        >
          View Sync details
        </button>
      </div>

      <Modal
        isOpen={isImportModalOpen}
        onClose={closeImportModal}
        title="Import users"
        subheading="Choose notification and template flags, then select an .xlsx file."
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={closeImportModal}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOpenImportConfirm}
              disabled={!pendingFile}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              Import
            </button>
          </div>
        }
      >
        <div className="space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-slate-700">Notification channel</legend>
              <div className="flex flex-col gap-2">
                {USER_IMPORT_NOTIFICATION_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-2 transition hover:bg-slate-50 has-[:checked]:border-indigo-300 has-[:checked]:bg-indigo-50/60"
                  >
                    <input
                      type="radio"
                      name="user-import-mode"
                      value={option.value}
                      checked={importSflag === option.value}
                      onChange={() => setImportSflag(option.value)}
                      className="h-4 w-4 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-800">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-slate-700">Email Template</legend>
              <div className="flex flex-col gap-2">
                {USER_IMPORT_TEMPLATE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-3 py-2 transition hover:bg-slate-50 has-[:checked]:border-indigo-300 has-[:checked]:bg-indigo-50/60"
                  >
                    <input
                      type="radio"
                      name="user-import-template"
                      value={option.value}
                      checked={importPriority === option.value}
                      onChange={() => setImportPriority(option.value)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-800">{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Spreadsheet</p>
            <input
              ref={modalFileInputRef}
              id="user-import-modal-file"
              type="file"
              accept=".xlsx"
              onChange={handleModalFileChange}
              className="sr-only"
            />
            <div className="flex flex-wrap items-center gap-3">
              <label
                htmlFor="user-import-modal-file"
                className="inline-flex cursor-pointer rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Choose file
              </label>
              <span className="text-sm text-slate-600">{pendingFile ? pendingFile.name : 'No file selected'}</span>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isImportConfirmOpen}
        onClose={() => setIsImportConfirmOpen(false)}
        title="Confirm import"
        subheading="Please review your selections before importing."
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={() => setIsImportConfirmOpen(false)}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleFinalImportConfirm}
              disabled={isLoading}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {isLoading ? 'Importing...' : 'Yes, import'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">Are you sure you want to import with the following settings?</p>
          <ul className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <li>
              <span className="font-medium text-slate-900">Notification channel:</span> {selectedNotificationLabel}
            </li>
            <li>
              <span className="font-medium text-slate-900">Email template:</span> {selectedTemplateLabel}
            </li>
            <li>
              <span className="font-medium text-slate-900">File:</span> {pendingFile?.name}
            </li>
          </ul>
        </div>
      </Modal>

      <Modal isOpen={isSyncDetailsOpen} onClose={() => setIsSyncDetailsOpen(false)} title="Sync details">
        <div className="space-y-3">
          {syncLogsQuery.isFetching && <p className="text-sm text-slate-600">Loading sync logs...</p>}
          {syncLogsQuery.error && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {(syncLogsQuery.error as Error).message}
            </p>
          )}
          {!syncLogsQuery.isFetching && !syncLogsQuery.error && (syncLogsQuery.data?.length ?? 0) === 0 && (
            <p className="text-sm text-slate-600">No sync logs found.</p>
          )}
          {(syncLogsQuery.data ?? []).map((detail) => (
            <div key={detail.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-900">{detail.fileName}</p>
              <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-slate-700 sm:grid-cols-2">
                <p>Rows processed: {detail.rowsProcessed}</p>
                <p>Failed sync count: {detail.failedSyncCount}</p>
                <p>Passed count: {detail.passedCount}</p>
                <p>Created at: {formatBackendDateTimeToLocal(detail.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
}

export default UserImportButton;
